import React from 'react';
import { IndexInputs as IndexInputsType } from '../Interfaces';
import { ValidatedInput } from '../Components';
import { Tooltip } from './Tooltip';

interface IndexInputsProps {
  inputs: IndexInputsType;
  onChange: (inputs: IndexInputsType) => void;
  getFieldError: (fieldName: string) => string | undefined;
  hasFieldError: (fieldName: string) => boolean;
}

export const IndexInputs: React.FC<IndexInputsProps> = ({
  inputs,
  onChange,
  getFieldError,
  hasFieldError
}) => {
  const handleChange = (field: keyof IndexInputsType, value: number | boolean) => {
    onChange({
      ...inputs,
      [field]: value,
    });
  };

  return (
    <div className={`controls index-controls ${hasFieldError('startAmount') || hasFieldError('startAge') || hasFieldError('endAge') || hasFieldError('monthlyAmount') || hasFieldError('annualIncrease') || hasFieldError('annualReturn') || hasFieldError('withdrawalRate') ? 'has-errors' : ''}`}>
      <h2>📈 Index Fund</h2>
      <div className="control-group">
        <ValidatedInput
          label="Starting Amount (£)"
          value={inputs.startAmount}
          onChange={(value) => handleChange('startAmount', value)}
          error={getFieldError('startAmount')}
          min="0"
          max="10000000"
        />

        <ValidatedInput
          label="Starting Age"
          value={inputs.startAge}
          onChange={(value) => handleChange('startAge', value)}
          error={getFieldError('startAge')}
          min="16"
          max="75"
        />

        <ValidatedInput
          label="Retirement Age"
          value={inputs.endAge}
          onChange={(value) => handleChange('endAge', value)}
          error={getFieldError('endAge')}
          min="50"
          max="85"
          tooltip="The UK State Pension age is currently 66, rising to 67 between 2026 and 2028. You can retire before or after this — this field is your personal target retirement age, not the State Pension age."
        />

        <ValidatedInput
          label="Monthly Contribution (£)"
          value={inputs.monthlyAmount}
          onChange={(value) => handleChange('monthlyAmount', value)}
          error={getFieldError('monthlyAmount')}
          min="1"
          max="40000"
        />

        <ValidatedInput
          label="Annual Increase (%)"
          value={inputs.annualIncrease}
          onChange={(value) => handleChange('annualIncrease', value)}
          error={getFieldError('annualIncrease')}
          step="0.5"
          min="0"
          max="20"
          tooltip="Increase your contributions each year to keep pace with wage growth. UK average annual wage growth has historically been 3–4%. Setting this helps reflect real-world contribution growth over your career."
        />

        <ValidatedInput
          label="Annual Return (%)"
          value={inputs.annualReturn}
          onChange={(value) => handleChange('annualReturn', value)}
          error={getFieldError('annualReturn')}
          step="0.1"
          min="0"
          max="25"
          tooltip="Global index funds such as the FTSE All-World or S&P 500 have historically returned around 8–10% per year before charges. Past performance is not a guarantee of future results."
        />

        <ValidatedInput
          label="Withdrawal Rate (%/year)"
          value={inputs.withdrawalRate}
          onChange={(value) => handleChange('withdrawalRate', value)}
          error={getFieldError('withdrawalRate')}
          step="0.5"
          min="0.1"
          max="15"
          tooltip="The percentage of your fund you withdraw each year in retirement. The 4% rule is a popular guideline — historically a 4% withdrawal from a diversified portfolio has often lasted 30+ years. Higher rates give more income but deplete the fund faster."
        />

        <div className="checkbox-group">
          <input
            type="checkbox"
            id="is-isa"
            checked={inputs.isISA}
            onChange={(e) => handleChange('isISA', e.target.checked)}
          />
          <label htmlFor="is-isa" className="checkbox-label">
            Stocks &amp; Shares ISA (tax-free withdrawals)
          </label>
          <Tooltip content="With a Stocks & Shares ISA you can invest up to £20,000 per year (2024/25 allowance). All growth and income are completely tax-free — there's no Capital Gains Tax or Income Tax on withdrawals, unlike a general investment account." />
        </div>
      </div>
    </div>
  );
};